<template>
  <Teleport to="body">
    <Transition :name="hasTransition ? 'bk-editable' : void 0">
      <Overlay v-if="editable" v-bind="editable" :key="key" @close="close" />
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  computed,
  ref,
  useBlokkli,
  watch,
  defineBlokkliFeature
} from "#imports";
import Overlay from "./Overlay/index.vue";
import onBlokkliEvent from "#blokkli/helpers/composables/onBlokkliEvent";
import defineCommands from "#blokkli/helpers/composables/defineCommands";
import { falsy } from "#blokkli/helpers";
defineBlokkliFeature({
  id: "editable-field",
  icon: "textbox",
  label: "Editable Field",
  requiredAdapterMethods: ["updateFieldValue", "getEditableFieldConfig"],
  description: "Implements a form overlay to edit a single field of a block."
});
const { selection, adapter, types, $t, dom, runtimeConfig, state } = useBlokkli();
const editable = ref(null);
const hasTransition = ref(false);
const key = computed(() => {
  if (!editable.value) {
    return "";
  }
  return editable.value.host.uuid + editable.value.fieldName;
});
const getHost = (uuid) => {
  if (uuid) {
    const block = dom.findBlock(uuid);
    if (block) {
      return block;
    }
  }
  return dom.findClosestEntityContext(dom.getActiveProviderElement());
};
const buildEditable = (fieldName, uuid) => {
  const host = getHost(uuid);
  if (!host) {
    return;
  }
  const hostEntityType = "type" in host ? host.type : runtimeConfig.itemEntityType;
  const hostEntityBundle = "bundle" in host ? host.bundle : host.itemBundle;
  const config = types.editableFieldConfig.forName(
    hostEntityType,
    hostEntityBundle,
    fieldName
  );
  if (!config) {
    let message = `Failed to load editable field config for field "${fieldName}" on entity type "${hostEntityType}" of bundle "${hostEntityBundle}"`;
    if (uuid) {
      message += ` with uuid "${uuid}"`;
    }
    throw new Error(message);
  }
  if (config.type === "frame" && !adapter.buildEditableFrameUrl) {
    return;
  }
  const hostElement = "itemBundle" in host ? host.element() : dom.getActiveProviderElement();
  const element = hostElement.querySelector(
    `[data-blokkli-editable-field="${fieldName}"]`
  );
  if (!(element instanceof HTMLElement)) {
    return;
  }
  return {
    fieldName,
    host,
    element,
    isComponent: element.dataset.blokkliEditableComponent === "true",
    value: element.dataset.blokkliEditableValue || "",
    config
  };
};
onBlokkliEvent("editable:focus", (e) => {
  if (!state.canEdit.value) {
    return;
  }
  hasTransition.value = !editable.value;
  editable.value = buildEditable(e.fieldName, e.uuid) || null;
  if (editable.value) {
    selection.editableActive.value = true;
  }
});
defineCommands(() => {
  const editables = selection.blocks.value.flatMap((v) => {
    return [...v.element().querySelectorAll("[data-blokkli-editable-field]")].map((el) => {
      if (!(el instanceof HTMLElement)) {
        return;
      }
      const block = el.closest("[data-uuid]");
      if (!(block instanceof HTMLElement)) {
        return;
      }
      if (block.dataset.uuid !== v.uuid) {
        return;
      }
      const name = el.dataset.blokkliEditableField;
      if (!name) {
        return;
      }
      return buildEditable(name, block.dataset.uuid);
    }).filter(falsy);
  });
  return editables.map((v) => {
    return {
      id: "feature:editable:edit:" + v.host.uuid + ":" + v.fieldName,
      group: "selection",
      label: $t("editableCommandEdit", 'Edit field "@name"').replace(
        "@name",
        v.config.label
      ),
      icon: "textbox",
      disabled: false,
      callback: () => {
        editable.value = v;
      }
    };
  });
});
watch(selection.editableActive, (isActive) => {
  if (!isActive) {
    hasTransition.value = true;
    editable.value = null;
  }
});
watch(editable, (v) => {
  if (!v && selection.editableActive.value) {
    selection.editableActive.value = false;
  }
});
const close = () => {
  editable.value = null;
  selection.editableActive.value = false;
};
</script>
